import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { fetchAnnouncements, createAnnouncement, deleteAnnouncement } from '@/api/admin'
import { Plus, Trash2, Megaphone, Loader2 } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { toast } from 'sonner'

const partnerTypes = [
  'Services Partner',
  'MSSP Partner', 
  'Reseller Partner'
]

export function AnnouncementsManager() {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const queryClient = useQueryClient()
  
  const { data, isLoading } = useQuery({
    queryKey: ['admin-announcements'],
    queryFn: fetchAnnouncements,
  })

  const createMutation = useMutation({
    mutationFn: createAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-announcements'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      setIsDialogOpen(false)
      toast.success('Announcement created')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-announcements'] })
      toast.success('Announcement deleted')
    },
  })

  if (isLoading) {
    return <div className="h-48 animate-pulse rounded-lg bg-muted" />
  }

  const announcements = data?.announcements || []

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Manage Announcements</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <NewAnnouncementForm 
              onSubmit={createMutation.mutate}
              isPending={createMutation.isPending}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-2">
        {announcements.map((announcement: any) => (
          <Card key={announcement.id}>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cloudflare-orange/10">
                <Megaphone className="h-5 w-5 text-cloudflare-orange" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium truncate">{announcement.title}</h3>
                <p className="text-sm text-muted-foreground line-clamp-1">
                  {announcement.body}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <Badge variant={announcement.priority === 'high' ? 'destructive' : 'secondary'}>
                    {announcement.priority}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(announcement.published_at)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Visible to: {announcement.visible_to_types?.join(', ')}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => deleteMutation.mutate(announcement.id)}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </CardContent>
          </Card>
        ))}
        
        {announcements.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center p-12 text-center">
              <Megaphone className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium">No announcements</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Create announcements to share news with partners
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

function NewAnnouncementForm({ onSubmit, isPending }: { onSubmit: (data: any) => void, isPending: boolean }) {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [priority, setPriority] = useState('medium')
  const [visibleTo, setVisibleTo] = useState<string[]>(partnerTypes)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({ title, body, priority, visible_to_types: visibleTo })
  }

  const togglePartnerType = (type: string) => {
    setVisibleTo(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <DialogHeader>
        <DialogTitle>Create Announcement</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium">Title</label>
          <Input 
            value={title} 
            onChange={(e) => setTitle(e.target.value)} 
            placeholder="Announcement title"
            required
          />
        </div>
        <div>
          <label className="text-sm font-medium">Body</label>
          <Textarea 
            value={body} 
            onChange={(e) => setBody(e.target.value)} 
            placeholder="Announcement content"
            rows={4}
            required
          />
        </div>
        <div>
          <label className="text-sm font-medium">Priority</label>
          <select 
            value={priority} 
            onChange={(e) => setPriority(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Visible To</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {partnerTypes.map((type) => (
              <label key={type} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={visibleTo.includes(type)}
                  onChange={() => togglePartnerType(type)}
                />
                <span className="text-sm">{type}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <Button type="submit" className="w-full" disabled={isPending}>
        {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Announcement'}
      </Button>
    </form>
  )
}
